import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Button,
  TextField,
  Box,
  Chip,
  Typography,
  InputAdornment,
} from "@mui/material";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Visibility as VisibilityIcon } from "@mui/icons-material";
import { format } from "date-fns";

const UserTable = ({
  users,
  title,
  onEdit,
  onDelete,
  onCreate,
  onView,
  loading = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  // Local filters: starts-with char, date range, sort
  const [startsWith, setStartsWith] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  // Make title optional and safe to use in string operations
  const displayTitle = title || "";
  const isClients = displayTitle.toLowerCase() === "clients";

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper to parse date-like fields
  const parseDateValue = (v) => {
    if (!v) return null;
    if (v.toDate && typeof v.toDate === "function") return v.toDate();
    if (v.seconds !== undefined && v.nanoseconds !== undefined)
      return new Date(v.seconds * 1000 + Math.floor(v.nanoseconds / 1000000));
    const d = new Date(v);
    if (!isNaN(d)) return d;
    return null;
  };

  // Filter and sort users based on search + filters
  const filteredUsers = (users || []).filter((user) => {
    const q = (searchTerm || "").toLowerCase();
    if (q) {
      const matches =
        (user.name || "").toLowerCase().includes(q) ||
        (user.email || "").toLowerCase().includes(q) ||
        (user.phone || "").includes(q) ||
        (user.businessName || "").toLowerCase().includes(q);
      if (!matches) return false;
    }

    if (startsWith && startsWith.trim()) {
      const ch = startsWith.trim().toLowerCase();
      if (!(user.name || "").toLowerCase().startsWith(ch)) return false;
    }

    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      const d =
        parseDateValue(user.createdAt) ||
        parseDateValue(user.updatedAt) ||
        null;
      if (!d) return false;
      if (from && d < from) return false;
      if (to) {
        const toEnd = new Date(to);
        toEnd.setHours(23, 59, 59, 999);
        if (d > toEnd) return false;
      }
    }

    return true;
  });

  // Sorting
  const compareByDate = (a, b, field = "createdAt") => {
    const pa = parseDateValue(a[field]) || parseDateValue(a.updatedAt) || null;
    const pb = parseDateValue(b[field]) || parseDateValue(b.updatedAt) || null;
    if (!pa && !pb) return 0;
    if (!pa) return 1;
    if (!pb) return -1;
    return pa - pb;
  };

  if (sortOption) {
    switch (sortOption) {
      case "newest":
        filteredUsers.sort((a, b) => compareByDate(b, a));
        break;
      case "oldest":
        filteredUsers.sort((a, b) => compareByDate(a, b));
        break;
      case "name_asc":
        filteredUsers.sort((a, b) =>
          ("" + (a.name || "")).localeCompare("" + (b.name || ""))
        );
        break;
      case "name_desc":
        filteredUsers.sort((a, b) =>
          ("" + (b.name || "")).localeCompare("" + (a.name || ""))
        );
        break;
      case "updated":
        filteredUsers.sort((a, b) => compareByDate(b, a, "updatedAt"));
        break;
      default:
        break;
    }
  }

  // Paginate filtered users
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateField) => {
    if (!dateField) return "N/A";

    // Handle Firestore Timestamp
    if (dateField.toDate && typeof dateField.toDate === "function") {
      return format(dateField.toDate(), "MMM dd, yyyy");
    }

    // Handle Date object
    if (dateField instanceof Date) {
      return format(dateField, "MMM dd, yyyy");
    }

    // Handle string dates
    if (typeof dateField === "string") {
      try {
        return format(new Date(dateField), "MMM dd, yyyy");
      } catch {
        return dateField;
      }
    }

    // Handle RN-style map objects: { day, month, year }
    if (
      typeof dateField === "object" &&
      dateField !== null &&
      dateField.day !== undefined &&
      dateField.month !== undefined &&
      (dateField.year !== undefined || dateField.Y !== undefined)
    ) {
      const day = String(dateField.day).padStart(2, "0");
      const month = String(dateField.month).padStart(2, "0");
      const year = String(dateField.year || dateField.Y);
      return `${month}/${day}/${year}`;
    }

    return "N/A";
  };

  const getRoleChips = (roles, approvalStatus) => {
    if (!roles) return null;

    const roleChips = Object.entries(roles)
      .filter(([role, value]) => value === true)
      .map(([role]) => (
        <Chip
          key={role}
          label={role}
          size="small"
          color={
            role === "admin"
              ? "error"
              : role === "client"
              ? "primary"
              : "default"
          }
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      ));

    // Add approval status chip for clients
    if (roles.client && approvalStatus) {
      const statusColor =
        approvalStatus === "approved"
          ? "success"
          : approvalStatus === "pending"
          ? "warning"
          : "error";
      roleChips.push(
        <Chip
          key="approval-status"
          label={approvalStatus}
          size="small"
          color={statusColor}
          sx={{ mr: 0.5, mb: 0.5 }}
        />
      );
    }

    return roleChips;
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" component="h2">
          {title}
        </Typography>
        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreate}
        >
          Add New
        </Button> */}
      </Box>

      <Box sx={{ p: 2, pt: 0 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            variant="outlined"
            placeholder={`Search ${
              displayTitle ? displayTitle.toLowerCase() : "users"
            }...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
            size="small"
          />

          {/* <TextField
            label="Starts with"
            value={startsWith}
            onChange={(e) => setStartsWith(e.target.value.slice(0, 1))}
            size="small"
            sx={{ width: 110 }}
          /> */}

          <TextField
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <TextField
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="table-sort-label">Sort</InputLabel>
            <Select
              labelId="table-sort-label"
              value={sortOption}
              label="Sort"
              onChange={(e) => setSortOption(e.target.value)}
            >
              <MenuItem value="newest">Date — Newest</MenuItem>
              <MenuItem value="oldest">Date — Oldest</MenuItem>
              <MenuItem value="name_asc">Name A → Z</MenuItem>
              <MenuItem value="name_desc">Name Z → A</MenuItem>
              <MenuItem value="updated">Recently Updated</MenuItem>
            </Select>
          </FormControl>

          <Button
            onClick={() => {
              setSearchTerm("");
              setStartsWith("");
              setDateFrom("");
              setDateTo("");
              setSortOption("newest");
              setPage(0);
            }}
            size="small"
          >
            Reset
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table stickyHeader aria-label="user table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Date of Birth</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell>Updated On</TableCell>
              <TableCell>Gender</TableCell>
              {/* <TableCell>Address</TableCell> */}
              {/* <TableCell>Roles</TableCell>
              {isClients && (
                <>
                  <TableCell>Business Name</TableCell>
                  <TableCell>Business Type</TableCell>
                  <TableCell>ABN</TableCell>
                </>
              )} */}
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : paginatedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {searchTerm
                    ? `No ${
                        displayTitle ? displayTitle.toLowerCase() : "users"
                      } found matching "${searchTerm}"`
                    : `No ${
                        displayTitle ? displayTitle.toLowerCase() : "users"
                      } found`}
                </TableCell>
              </TableRow>
            ) : (
              paginatedUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name || "N/A"}</TableCell>
                  <TableCell>{user.email || "N/A"}</TableCell>
                  <TableCell>{user.phone || "N/A"}</TableCell>
                  <TableCell>{formatDate(user.dob)}</TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>{formatDate(user.updatedAt)}</TableCell>
                  <TableCell>{user.gender || "N/A"}</TableCell>
                  {/* <TableCell>
                    {user.address ? (
                      typeof user.address === 'string' ? (
                        user.address
                      ) : (
                        <div>
                          <div>{user.address.street}</div>
                          <div>{user.address.city}, {user.address.state} {user.address.zipCode}</div>
                        </div>
                      )
                    ) : 'N/A'}
                  </TableCell> */}
                  {/* <TableCell>{getRoleChips(user.roles, user.approvalStatus)}</TableCell> */}
                  {/* {isClients && (
                    <>
                      <TableCell>{user.businessName || 'N/A'}</TableCell>
                      <TableCell>{user.businessType || 'N/A'}</TableCell>
                      <TableCell>{user.abn || 'N/A'}</TableCell>
                    </>
                  )} */}
                  <TableCell align="center">
                    {onEdit && (
                      <IconButton
                        color="primary"
                        onClick={() => onEdit(user)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {/* <Button size="small" onClick={() => onView && onView(user)} sx={{ mr: 1 }}>View</Button> */}
                    <IconButton
                      color="info"
                      onClick={() => onView && onView(user)}
                      title="View Provider"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {/* <IconButton
                      color="error"
                      onClick={() => onDelete(user)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton> */}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default UserTable;
